import type {
  ModuleId,
  PermissionLevel,
  PhamViDuLieu,
  RoleKey,
  RoleWithPermissions,
} from '@qlk/shared';
import {
  MODULE_IDS,
  MODULE_LABEL,
  PERMISSION_LABEL,
  PERMISSION_LEVELS,
  PHAM_VI_DU_LIEU,
  PHAM_VI_DU_LIEU_LABEL,
  PHAM_VI_DU_LIEU_MO_TA,
  ROLE_LABEL,
  coLocKenh,
  thongDiepAssignedKhongHopLe,
  thongDiepPhamViToanCongTy,
} from '@qlk/shared';
import { useState } from 'react';

import { useLuuQuyen } from '../api.js';

/** Thứ tự dòng bám theo thứ tự menu trong ảnh. */
const THU_TU: ModuleId[] = [
  'dashboard',
  'channels',
  'networks',
  'production',
  'schedule',
  'music',
  'analytics',
  'revenue',
  'compliance',
  'team',
  'sync',
  'core',
];

const MAU_MUC: Record<PermissionLevel, { cham: string; chu: string }> = {
  admin: { cham: 'var(--color-cham-ok)', chu: 'var(--color-ok-chu)' },
  edit: { cham: 'var(--color-cham-info)', chu: 'var(--color-info-chu)' },
  assigned: { cham: 'var(--color-cham-warn)', chu: 'var(--color-warn-chu)' },
  view: { cham: 'var(--color-cham-mo)', chu: 'var(--color-neutral-chu)' },
  none: { cham: 'var(--color-cham-tat)', chu: 'var(--color-chu-phu)' },
};

function OQuyen({
  muc,
  coSua,
  onDoi,
  nhan,
  lyDoKhoaAssigned,
}: {
  muc: PermissionLevel;
  coSua: boolean;
  onDoi: (muc: PermissionLevel) => void;
  nhan: string;
  /**
   * Module không lọc theo kênh ⇒ khoá ô "Kênh được giao" và nói rõ vì sao.
   * `null` = module có lọc, mọi mức đều chọn được. Máy chủ vẫn chặn lại bằng
   * `updateRolePermissionsInputSchema` (rà soát 16/10/2026, mục V4).
   */
  lyDoKhoaAssigned: string | null;
}) {
  const mau = MAU_MUC[muc];

  if (!coSua) {
    return (
      <span className="inline-flex items-center gap-2 text-co-ban" style={{ color: mau.chu }}>
        <span
          className="inline-block size-2 rounded-full"
          style={{ backgroundColor: mau.cham }}
          aria-hidden="true"
        />
        {PERMISSION_LABEL[muc]}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="inline-block size-2 shrink-0 rounded-full"
        style={{ backgroundColor: mau.cham }}
        aria-hidden="true"
      />
      <select
        aria-label={nhan}
        value={muc}
        onChange={(e) => {
          onDoi(e.target.value as PermissionLevel);
        }}
        className="vung-bam rounded-[10px] border border-transparent bg-transparent px-1 text-co-ban hover:border-vien"
        style={{ color: mau.chu }}
        {...(lyDoKhoaAssigned === null ? {} : { title: lyDoKhoaAssigned })}
      >
        {PERMISSION_LEVELS.map((m) => (
          <option
            key={m}
            value={m}
            disabled={m === 'assigned' && lyDoKhoaAssigned !== null}
            {...(m === 'assigned' && lyDoKhoaAssigned !== null ? { title: lyDoKhoaAssigned } : {})}
          >
            {PERMISSION_LABEL[m]}
            {m === 'assigned' && lyDoKhoaAssigned !== null ? ' — không áp dụng' : ''}
          </option>
        ))}
      </select>
    </span>
  );
}

/**
 * Ô "Phạm vi dữ liệu" của một vai trò.
 *
 * Tách khỏi `OQuyen` vì đây **không** phải một mức quyền: nó trả lời câu hỏi
 * khác ("trên dữ liệu của ai") và chỉ có hai giá trị. Gộp chung một ô chọn 7
 * mục sẽ làm hai trục khác nhau trông như một thang duy nhất.
 */
function OPhamVi({
  phamVi,
  coSua,
  nhan,
  canhBao,
  onDoi,
}: {
  phamVi: PhamViDuLieu;
  coSua: boolean;
  nhan: string;
  /** `true` = vai trò không phải quản trị mà đang để "Toàn công ty". */
  canhBao: boolean;
  onDoi: (p: PhamViDuLieu) => void;
}) {
  const mau = canhBao ? 'var(--color-bad-chu)' : 'var(--color-chu)';

  if (!coSua) {
    return (
      <span className="text-co-ban font-medium" style={{ color: mau }}>
        {PHAM_VI_DU_LIEU_LABEL[phamVi]}
      </span>
    );
  }

  return (
    <select
      aria-label={nhan}
      value={phamVi}
      onChange={(e) => {
        onDoi(e.target.value as PhamViDuLieu);
      }}
      title={PHAM_VI_DU_LIEU_MO_TA[phamVi]}
      className="vung-bam rounded-[10px] border border-vien bg-transparent px-1 text-co-ban font-medium"
      style={{ color: mau }}
    >
      {PHAM_VI_DU_LIEU.map((p) => (
        <option key={p} value={p}>
          {PHAM_VI_DU_LIEU_LABEL[p]}
        </option>
      ))}
    </select>
  );
}

export interface MaTranQuyenProps {
  vaiTro: RoleWithPermissions[];
  /**
   * Chỉ **`core:admin`** được sửa (không phải `team:admin`): sửa ma trận là tự
   * nâng quyền cho mình được — rà soát bảo mật 16/10/2026, mục V11. Server chặn
   * bằng `requirePermission('core', 'admin')`, đây chỉ là ẩn ô chọn.
   */
  coSua: boolean;
  /**
   * Vai trò **người đang xem** đang giữ. Cột của chính những vai trò này không
   * cho sửa: server trả 400 nếu cố (cùng mục V11).
   */
  vaiTroCuaToi?: readonly RoleKey[];
}

export function MaTranQuyen({ vaiTro, coSua, vaiTroCuaToi = [] }: MaTranQuyenProps) {
  const luu = useLuuQuyen();
  const [loi, setLoi] = useState<string | null>(null);

  function doi(role: RoleWithPermissions, module: ModuleId, muc: PermissionLevel) {
    setLoi(null);
    luu.mutate(
      { id: role.id, permissions: { ...role.permissions, [module]: muc } },
      {
        onError: (err) => {
          setLoi(err.message);
        },
      },
    );
  }

  /*
   * Gửi kèm cả `permissions` hiện tại chứ không chỉ `phamViDuLieu`: endpoint
   * nhận một thân duy nhất cho cả hai nửa, và gửi nguyên trạng ma trận là
   * thao tác không đổi (idempotent) với phần đó.
   */
  function doiPhamVi(role: RoleWithPermissions, phamViDuLieu: PhamViDuLieu) {
    setLoi(null);
    luu.mutate(
      { id: role.id, permissions: role.permissions, phamViDuLieu },
      {
        onError: (err) => {
          setLoi(err.message);
        },
      },
    );
  }

  const dong = THU_TU.filter((m) => (MODULE_IDS as readonly string[]).includes(m));

  /**
   * Vai trò **không phải quản trị** mà đang để "Toàn công ty" — tức là người
   * giữ nó thấy mọi kênh. Đây là đúng cấu hình đã gây ra sự cố trên bản chạy
   * thật, nên nó được nói thành một dải đỏ chứ không chỉ một ô màu.
   */
  const vaiTroMoToang = vaiTro.filter(
    (r) => r.key !== 'admin' && r.phamViDuLieu === 'toan_cong_ty',
  );

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-tieu-de-hop font-bold text-chu">Ma trận phân quyền</h2>

        <ul className="flex flex-wrap items-center gap-4">
          {(['admin', 'edit', 'assigned', 'view'] as const).map((m) => (
            <li key={m} className="flex items-center gap-2 text-co-ban text-chu-phu">
              <span
                className="inline-block size-2 rounded-full"
                style={{ backgroundColor: MAU_MUC[m].cham }}
                aria-hidden="true"
              />
              {PERMISSION_LABEL[m]}
            </li>
          ))}
        </ul>
      </header>

      {loi && <p className="mb-3 text-co-ban text-bad-chu">{loi}</p>}

      {vaiTroMoToang.length > 0 && (
        <div
          role="alert"
          className="mb-4 rounded-the border border-bad-chu bg-bad-nen px-4 py-3 text-co-ban text-bad-chu"
        >
          <p className="font-bold">Cảnh báo phạm vi dữ liệu</p>
          <ul className="mt-1 list-disc pl-5">
            {vaiTroMoToang.map((r) => (
              <li key={r.id}>{thongDiepPhamViToanCongTy(ROLE_LABEL[r.key])}</li>
            ))}
          </ul>
          <p className="mt-1">
            Chỉ để "Toàn công ty" cho vai trò thật sự cần nhìn số tổng (ví dụ Kế toán chốt doanh
            thu). Các vai trò còn lại nên để "Chỉ kênh được giao".
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[840px] border-collapse text-co-ban">
          <caption className="sr-only">Mức quyền của từng vai trò theo module</caption>
          <thead>
            <tr className="bg-header-bang">
              <th scope="col" className="px-4 py-3 text-left font-medium text-chu-phu">
                Module
              </th>
              {vaiTro.map((r) => (
                <th key={r.id} scope="col" className="px-4 py-3 text-left font-medium text-chu-phu">
                  {ROLE_LABEL[r.key]}
                  {coSua && vaiTroCuaToi.includes(r.key) && (
                    <span className="block font-normal text-chu-phu">
                      (vai trò của bạn — nhờ quản trị khác đổi)
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-vien bg-header-bang">
              <th scope="row" className="px-4 py-3 text-left font-bold text-chu">
                Phạm vi dữ liệu
                <span className="block font-normal text-chu-phu">
                  Quyết định việc lọc theo kênh ở mọi module
                </span>
              </th>
              {vaiTro.map((r) => (
                <td key={r.id} className="px-4 py-3">
                  <OPhamVi
                    phamVi={r.phamViDuLieu}
                    coSua={coSua && !vaiTroCuaToi.includes(r.key)}
                    nhan={`${ROLE_LABEL[r.key]} — Phạm vi dữ liệu`}
                    canhBao={r.key !== 'admin' && r.phamViDuLieu === 'toan_cong_ty'}
                    onDoi={(p) => {
                      doiPhamVi(r, p);
                    }}
                  />
                </td>
              ))}
            </tr>
            {dong.map((module) => (
              <tr key={module} className="border-t border-vien">
                <th scope="row" className="px-4 py-3 text-left font-normal text-chu">
                  {MODULE_LABEL[module]}
                  {!coLocKenh(module) && (
                    <span
                      className="block font-normal text-chu-phu"
                      title={thongDiepAssignedKhongHopLe(module)}
                    >
                      Không lọc theo kênh
                    </span>
                  )}
                </th>
                {vaiTro.map((r) => (
                  <td key={r.id} className="px-4 py-3">
                    <OQuyen
                      muc={r.permissions[module] ?? 'none'}
                      coSua={coSua && !vaiTroCuaToi.includes(r.key)}
                      nhan={`${ROLE_LABEL[r.key]} — ${MODULE_LABEL[module]}`}
                      lyDoKhoaAssigned={
                        coLocKenh(module) ? null : thongDiepAssignedKhongHopLe(module)
                      }
                      onDoi={(muc) => {
                        doi(r, module, muc);
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
